import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

const DataWarehouseSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [viewportHeight, setViewportHeight] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsSmallMobile(width <= 375); // Specific to iPhone SE and similar small devices
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    section: {
      minHeight: '100vh',
      width: '100%',
      margin: 0,
      padding: isSmallMobile ? '10px' : (isMobile ? '20px' : '30px'),
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      transition: 'all 0.5s ease-out',
      opacity: inView ? 1 : 0,
    },
    mainContainer: {
      width: '100%',
      maxWidth: '1400px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: isSmallMobile ? '15px' : '30px',
      position: 'relative',
      zIndex: 2,
      padding: isSmallMobile ? '0 10px' : '0',
    },
    headingContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2px 0',
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(-20px)',
      transition: 'all 0.8s ease-out',
    },
    heading: {
      fontSize: isSmallMobile ? '28px' : (isMobile ? '38px' : '52px'),
      fontWeight: '700',
      fontFamily: '"Oswald", sans-serif',
      color: '#400684',
      margin: '0 auto',
      lineHeight: '1.2',
      textAlign: 'center',
      padding: '0 10px',
      position: 'relative',
    },
    headingLine: {
      width: isSmallMobile ? '180px' : '250px',
      height: '3px',
      backgroundColor: '#400684',
      margin: '10px auto 0',
      transition: 'width 0.3s ease',
    },
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: isSmallMobile ? '15px' : '20px',
      alignItems: 'center',
      width: '100%',
    },
    leftContent: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: isSmallMobile ? '10px' : '15px',
      transition: 'all 0.8s ease-out',
      transform: inView ? 'translateX(0)' : 'translateX(-40px)',
      opacity: inView ? 1 : 0,
    },
    bulletPoints: {
      listStyle: 'none',
      padding: 0,
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      gap: isSmallMobile ? '6px' : '8px',
      overflow: 'hidden',
    },
    bulletPoint: {
      color: '#424242',
      fontSize: isSmallMobile ? '12px' : '14px',
      position: 'relative',
      paddingLeft: '20px',
      lineHeight: '1.4',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'rgb(189, 164, 219)',
      padding: isSmallMobile ? '6px 10px 6px 20px' : '8px 12px 8px 24px',
      borderRadius: '6px',
      transition: 'all 0.3s ease',
    },
    bulletPointBefore: {
      content: '""',
      position: 'absolute',
      left: '8px',
      width: '5px',
      height: '5px',
      backgroundColor: '#400684',
      borderRadius: '50%',
    },
    description: {
      color: '#666',
      fontSize: isSmallMobile ? '11px' : '13px',
      lineHeight: '1.6',
      margin: isSmallMobile ? '8px 0' : '10px 0',
      padding: isSmallMobile ? '8px' : '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(34, 211, 238, 0.2)',
    },
    button: {
      padding: isSmallMobile ? '8px 15px' : '10px 20px',
      backgroundColor: '#400684',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      width: 'fit-content',
      fontSize: isSmallMobile ? '12px' : '14px',
      fontFamily: '"Oswald", sans-serif',
      alignSelf: 'center',
    },
    rightContent: {
      width: '100%',
      height: isSmallMobile ? '200px' : '300px',
      position: 'relative',
      transition: 'all 0.8s ease-out',
      transform: inView ? 'translateX(0)' : 'translateX(40px)',
      opacity: inView ? 1 : 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    gifWrapper: {
      width: '100%',
      height: '100%',
      borderRadius: '16px',
      overflow: 'hidden',
      backgroundColor: 'white',
      padding: isSmallMobile ? '8px' : '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    gif: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      borderRadius: '8px',
    }
  };

  return (
    <div ref={ref} style={styles.section}>
      <div style={styles.mainContainer}>
        <div style={styles.headingContainer}>
          <h2 style={styles.heading}>DATA WAREHOUSING</h2>
          <div style={styles.headingLine}></div>
        </div>
        
        <div style={styles.contentContainer}>
          <div style={styles.leftContent}>
            <ul style={styles.bulletPoints}>
              {[
                "Enterprise BigData Cloud Solutions",
                "EXADATA & Oracle BI Implementation",
                "Cloud Infrastructure",
                "Big Data Management"
              ].map((text, index) => (
                <li key={index} style={{...styles.bulletPoint}}>
                  <span style={styles.bulletPointBefore}></span>
                  {text}
                </li>
              ))}
            </ul>
            <p style={styles.description}>
              Transform your business intelligence with our comprehensive data analysis solutions. 
              We deliver actionable insights through rigorous analysis and strategic data management.
            </p>
            <button 
              style={styles.button}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#400684';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(34, 211, 238, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#400684';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 14px rgba(34, 211, 238, 0.3)';
              }}
            >
              Contact Us
            </button>
          </div>

          <div style={styles.rightContent}>
            <div style={styles.gifWrapper}>
              <img 
                src="/database-unscreen.gif"
                alt="Advanced Data Warehouse Solutions"
                style={styles.gif}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataWarehouseSection;